import React from 'react';
import { User, Building, Calendar, Phone, Mail, Lock, Eye, EyeOff, CheckCircle, Hash, CreditCard } from 'lucide-react';
import { ProfessionalInput, ProfessionalSelect } from '../common/ProfessionalInput';

const BasicInfoStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const businessTypes = [
    'Sole Proprietorship',
    'Partnership', 
    'Private Limited',
    'LLP',
    'Public Limited'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Basic Information</h2>
        <p className="text-gray-600">Please provide your personal and business details</p>
      </div>

      {/* Personal Information */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <User className="w-5 h-5 mr-2 text-blue-600" />
          Personal Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ProfessionalInput
            label="Full Name"
            type="text"
            name="fullName"
            id="fullName"
            autoComplete="name"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            placeholder="Enter your full name"
            icon={User}
            required
            error={errors.fullName}
          />

          <ProfessionalInput
            label="Email Address"
            type="email"
            name="email"
            id="email"
            autoComplete="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your.email@example.com"
            icon={Mail}
            required
            error={errors.email}
          />

          <div className="space-y-2">
            <ProfessionalInput
              label="Phone Number"
              type="tel"
              name="phone"
              id="phone"
              autoComplete="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="10-digit mobile number"
              icon={Phone}
              required
              disabled={!!formData.phone}
              error={errors.phone}
              maxLength="10"
            />
            {formData.phone && (
              <div className="flex items-center text-sm text-green-600">
                <CheckCircle className="w-4 h-4 mr-2" />
                Phone number verified
              </div>
            )}
          </div>


          <ProfessionalInput
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Create a strong password"
            icon={Lock}
            required
            error={errors.password}
          />

          <ProfessionalInput
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            placeholder="Confirm your password"
            icon={Lock}
            required
            error={errors.confirmPassword}
          />
        </div>
      </div>

      {/* Business Information */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Building className="w-5 h-5 mr-2 text-blue-600" />
          Business Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ProfessionalInput
            label="Business Name"
            type="text"
            value={formData.businessName}
            onChange={(e) => handleInputChange('businessName', e.target.value)}
            placeholder="Enter your business name"
            icon={Building}
            required
            error={errors.businessName}
          />

          <ProfessionalSelect
            label="Business Type"
            value={formData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
            placeholder="Select business type"
            options={businessTypes}
            required
            error={errors.businessType}
          />

          <ProfessionalInput
            label="GST Number"
            type="text"
            value={formData.gstNumber}
            onChange={(e) => handleInputChange('gstNumber', e.target.value.toUpperCase())}
            placeholder="27AAACH7409R1ZZ"
            icon={Hash}
            required
            error={errors.gstNumber}
            maxLength="15"
          />

          <ProfessionalInput
            label="PAN Number"
            type="text"
            value={formData.panNumber}
            onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
            placeholder="AAACH7409R"
            icon={CreditCard}
            required
            error={errors.panNumber}
            maxLength="10"
          />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Important Information
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Ensure all information matches your official documents</li>
                <li>GST and PAN numbers will be verified during document upload</li>
                <li>You can save your progress and continue later</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;
